import { db } from "./index";
import { doc, getDoc } from "firebase/firestore";

/**
 * Check if a user can access specific content based on their role and content settings
 * @param {string} contentId - The ID of the content in the content_settings collection
 * @param {string} userRole - The user's role ('admin', 'CI', 'IC', or 'visitor')
 * @returns {Promise<boolean>} - Whether the user can access the content
 */
export async function canAccessContent(contentId, userRole) {
  try {
    // Normalize user role to lowercase for case-insensitive comparison
    const normalizedRole = userRole?.toLowerCase();

    // Get content settings from Firestore
    const docRef = doc(db, "content_settings", contentId);
    const docSnap = await getDoc(docRef);

    // If document doesn't exist, only admins can see
    if (!docSnap.exists()) {
      const canAccess = normalizedRole === "admin";
      return canAccess;
    }

    const settings = docSnap.data();


    // If content is disabled, only admins can see
    if (!settings.enabled) {
      const canAccess = normalizedRole === "admin";
      return canAccess;
    }

    // If visibility is "visitors", everyone can access
    if (settings.visibility?.toLowerCase() === "visitors") {
      return true;
    }

    // Role hierarchy: visitor < IC < CI < admin
    // IC = Investor Candidate (can access IC-level content)
    // CI = Confirmed Investor (can access IC + CI-level content)

    // If visibility is "IC", IC, CI, and admins can access
    if (settings.visibility?.toLowerCase() === "ic") {
      const canAccess = normalizedRole === "ic" || normalizedRole === "ci" || normalizedRole === "admin";
      return canAccess;
    }

    // If visibility is "CI", only CI and admins can access
    if (settings.visibility?.toLowerCase() === "ci") {
      const canAccess = normalizedRole === "ci" || normalizedRole === "admin";
      return canAccess;
    }

    // Legacy: "VIP" treated as equivalent to "IC" for backwards compatibility
    if (settings.visibility?.toLowerCase() === "vip") {
      const canAccess = normalizedRole === "ic" || normalizedRole === "ci" || normalizedRole === "vip" || normalizedRole === "admin";
      return canAccess;
    }

    // Default case - if we don't recognize the visibility setting
    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Get the user's role from Firestore
 * @param {string} userId - The user's ID
 * @returns {Promise<string>} - The user's role ('admin', 'CI', 'IC', or 'visitor')
 */
export async function getUserRole(userId) {
  try {
    if (!userId) {
      return "visitor";
    }

    const userDoc = await getDoc(doc(db, "users", userId));

    if (!userDoc.exists()) {
      return "visitor";
    }

    const userData = userDoc.data();
    const role = userData.privileges || "visitor";
    return role;
  } catch (error) {
    return "visitor";
  }
}
